import { getLocaleString } from '@assets/i18n/config';
import type { WatchlistAccount, WatchlistTrigger } from '@interfaces/watchList';
import { Chip } from '@mui/material';
import type { ColumnDef } from '@ovComponents/2-component/dataTable';
import { AssetProgressBar } from '@ovComponents/3-pattern/assetProgressBar/assetProgressBar';
import { currencySymbol } from '@utils/currency';
import { isCurrencySymbolAfter } from '@utils/moneyFormatting';
import { Box, Tooltip, Typography, useTheme } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { WATCH_LIST_TRIGGER_REASON_KEYS } from './watchListCopy';

export function formatWatchListTriggerReason(
  reason: string,
  t: TFunction<'watchList'>,
): string {
  const key = WATCH_LIST_TRIGGER_REASON_KEYS[reason];
  return key ? t(key) : reason;
}

/**
 * Returns display props for the reason chip.
 * Kept as a helper so the label logic is testable in isolation.
 */
export function getReasonChipProps(
  trigger: WatchlistTrigger,
  t?: TFunction<'watchList'>,
): {
  label: string;
  tooltip?: string;
} {
  const label = t
    ? formatWatchListTriggerReason(trigger.reason, t)
    : trigger.reason;

  if (!trigger.occurredAt) {
    return { label };
  }

  const tooltip = new Intl.DateTimeFormat(getLocaleString(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(trigger.occurredAt));

  return { label, tooltip };
}

/**
 * Column definitions for the Watch List DataTable.
 *
 * Rendered as a hook so column cells can read theme tokens.
 *
 * Notes:
 * - The reason cell uses a plain MUI `<Chip variant='outlined'>` rather than `AlertChip`
 *   because `AlertChip` only accepts 'checkmark' | 'problem' | 'cancel' icons — none of
 *   which are neutral/informational. A follow-up PR should extend `AlertChip` with an
 *   `icon='info' | 'none'` option, at which point this cell can switch to `<AlertChip>`.
 * - `bandConsumedPercentage` = max(cash, position) — the worst-of dimension. This is
 *   the sort key; the bar renders both dimensions side-by-side.
 */
export function useWatchListColumns(): ColumnDef<WatchlistAccount, unknown>[] {
  const theme = useTheme();
  const { t } = useTranslation(['watchList', 'accountTypes']);

  const columns: ColumnDef<WatchlistAccount, unknown>[] = [
    {
      id: 'portfolio',
      accessorFn: (row) => row.portfolioName,
      header: t('watchList:table.portfolio'),
      enableSorting: true,
      cell: ({ row }) => (
        <Box>
          <Typography
            variant='bodySmall'
            component='div'
            sx={{ fontWeight: 600 }}
          >
            {row.original.portfolioName}
          </Typography>
          <Typography
            variant='labelSmall'
            component='div'
            color='textSecondary'
          >
            {row.original.accountNumber} · {row.original.householdName}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'accountType',
      accessorKey: 'accountType',
      header: t('watchList:table.accountType'),
      enableSorting: false,
      cell: ({ getValue }) => (
        <Box
          sx={{
            display: 'inline-flex',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: theme.color.primaryVariant,
          }}
        >
          <Typography
            variant='labelSmall'
            sx={{ color: theme.color.onPrimaryVariant }}
          >
            {t(`accountTypes:${getValue<string>()}`, {
              defaultValue: getValue<string>(),
            })}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'reason',
      accessorFn: (row) => row.trigger.reason,
      header: t('watchList:table.reason'),
      enableSorting: false,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const { label, tooltip } = getReasonChipProps(row.original.trigger, t);
        const chip = (
          <Chip
            variant='outlined'
            size='small'
            label={label}
            sx={{ fontWeight: 600, fontSize: 12 }}
          />
        );

        return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
      },
    },
    {
      id: 'custodian',
      accessorKey: 'custodian',
      header: t('watchList:table.custodian'),
      enableSorting: true,
    },
    {
      id: 'portfolioValue',
      accessorKey: 'portfolioValue',
      header: t('watchList:table.value'),
      enableSorting: true,
      meta: { dataTable: { align: 'right', numeric: true } },
      cell: ({ getValue }) => {
        const locale = getLocaleString();
        const value = getValue<number>();
        const symbol = currencySymbol.USD ?? '$';
        const formatted = new Intl.NumberFormat(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(Math.abs(value));
        const sign = value < 0 ? '-' : '';
        return isCurrencySymbolAfter(locale)
          ? `${sign}${formatted} ${symbol}`
          : `${sign}${symbol}${formatted}`;
      },
    },
    {
      id: 'bandConsumed',
      accessorKey: 'bandConsumedPercentage',
      header: t('watchList:table.bandConsumed'),
      enableSorting: true,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}
        >
          <AssetProgressBar
            data={[
              {
                key: 'cash',
                percentage: row.original.bandConsumedCashPercentage,
                colorCode: theme.color.warning ?? '#ed6c02',
              },
              {
                key: 'position',
                percentage: row.original.bandConsumedPositionPercentage,
                colorCode: theme.color.warning ?? '#ed6c02',
              },
            ]}
            sx={{ flex: 1 }}
          />
          <Typography
            variant='labelSmall'
            sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            {`${Math.round(row.original.bandConsumedPercentage)}%`}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'threshold',
      accessorKey: 'thresholdLabel',
      header: t('watchList:table.threshold'),
      enableSorting: false,
      cell: ({ getValue }) => (
        <Typography variant='bodySmall'>{getValue<string>()}</Typography>
      ),
    },
  ];

  return columns;
}
